import { useEffect, useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { FullPageDotsLoader } from "@/clientWeb/components/ui/page-loader"
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts"
import {
  ArrowRight, Users, Video, Clock, Download,
  MessageSquare, ClipboardList, Mic,
} from "lucide-react"
import { webinarAnalyticsService, type WebinarAnalyticsSnapshot } from "./services/webinar-analytics.service"

const COLORS = ["#0ea5e9", "#8b5cf6", "#f97316", "#10b981", "#ec4899", "#f59e0b"]

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-800 dark:text-white mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.fill || p.stroke }} className="mt-0.5">
          {p.name}: <strong>{p.value}{p.unit}</strong>
        </p>
      ))}
    </div>
  )
}

export default function WebinarAnalyticsPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const [d, setD] = useState<WebinarAnalyticsSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryNonce, setRetryNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    webinarAnalyticsService
      .getSnapshot(id ?? '')
      .then((snap) => { if (!cancelled) setD(snap) })
      .catch((err) => {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : 'Failed to load analytics'
        setError(msg)
        toast.error({ title: t('common.error'), description: msg })
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id, t, retryNonce])

  if (loading || (!d && !error)) return <FullPageDotsLoader />

  if (error || !d) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-sm w-full bg-card border border-border rounded-2xl shadow-xl p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <Download className="w-6 h-6 text-red-600 dark:text-red-400 rotate-180" />
          </div>
          <h3 className="text-base font-semibold text-foreground mb-1">Couldn't load analytics</h3>
          <p className="text-sm text-muted-foreground mb-4">{error ?? 'Something went wrong.'}</p>
          <div className="flex gap-2">
            <button onClick={() => navigate(-1)} className="flex-1 px-4 py-2 rounded-xl border border-border text-sm font-medium">Go back</button>
            <button onClick={() => setRetryNonce(n => n + 1)} className="flex-1 px-4 py-2 rounded-xl bg-primary text-white text-sm font-medium">Retry</button>
          </div>
        </div>
      </div>
    )
  }
  const attendancePct = ((d.attendees / d.registrations) * 100).toFixed(1)

  const statCards = [
    {
      label: t("webinar.registrations"), value: d.registrations, route: "attendees",
      color: "bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20", text: "text-blue-600 dark:text-blue-400",
      icon: <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
    },
    {
      label: t("webinar.attendees"), value: d.attendees, route: "attendees?tab=Attended",
      color: "bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20", text: "text-emerald-600 dark:text-emerald-400",
      icon: <Users className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />,
    },
    {
      label: t("webinar.panelists"), value: d.panelists, route: "panelists",
      color: "bg-gradient-to-br from-purple-50 to-violet-100 dark:from-purple-900/20 dark:to-violet-900/20", text: "text-purple-600 dark:text-purple-400",
      icon: <Mic className="w-5 h-5 text-purple-600 dark:text-purple-400" />,
    },
    {
      label: t("webinar.recordingViews"), value: d.recordingViews, route: "recordings",
      color: "bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20", text: "text-amber-600 dark:text-amber-400",
      icon: <Video className="w-5 h-5 text-amber-600 dark:text-amber-400" />,
    },
  ]

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-gradient-to-br from-slate-50 via-purple-50/30 to-pink-50/20 dark:from-slate-900 dark:via-slate-800/50 dark:to-slate-900 p-4 md:p-6 gap-5">

      {/* Advanced Breadcrumb */}
      <div className="relative">
        <div className="flex items-center gap-1 mb-2">


          <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
            <button onClick={() => navigate("/webinar")} className="text-primary font-semibold transition-colors flex items-center gap-1">
              {t("webinar.webinars")}
            </button>
            <span>/</span>
            <button onClick={() => navigate(`/webinar/${id}`)} className="text-primary font-semibold transition-colors">{t("webinar.webinarDetails")}</button>
            <span>/</span>
            <span className="text-slate-700 dark:text-slate-200 font-semibold">{t("nav.analytics")}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{d.title}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {d.date} &nbsp;·&nbsp; {d.startTime} – {d.endTime} &nbsp;·&nbsp; <span className="text-emerald-600 font-medium">{attendancePct}% {t("webinar.attendance")}</span>
            </p>
          </div>
          <button
            className="flex items-center gap-2 bg-gradient-to-r from-primary to-purple-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:shadow-lg transition-all self-start sm:self-auto shrink-0"
            onClick={() => toast.success({ title: t("webinar.exportReport"), description: `${d.title} — ${t("common.downloadStarted") ?? "Downloading…"}` })}
          >
            <Download className="w-4 h-4" /> {t("webinar.exportReport")}
          </button>
        </div>
      </div>

      {/* Stat Cards - aligned with Meetings Analytics dashboard */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map(c => (
          <button
            key={c.label}
            onClick={() => navigate(`/webinar/${id}/analytics/${c.route}`)}
            className="group relative bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl px-4 py-4 shadow-xs hover:shadow-md hover:border-primary/30 transition-all text-left overflow-hidden"
          >
            {/* Decorative Background Wave */}
            <div className={`absolute inset-0 ${c.color}`}>
              <svg
                className="absolute bottom-0 right-0 w-full h-full"
                viewBox="0 0 200 120"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z"
                  fill="currentColor"
                  className="text-white/30 dark:text-white/10"
                />
              </svg>
            </div>

            {/* Content aligned with StatCard */}
            <div className="relative z-10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-card/60 dark:bg-card/10 flex items-center justify-center shrink-0">
                {c.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="text-2xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white leading-none truncate">
                    {c.value}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary transition-colors" />
                </div>
                <div className="text-sm font-medium text-slate-600 dark:text-slate-400 truncate">
                  {c.label}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Attendance Rate + Viewer Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Attendance Rate */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-slate-800/80 dark:via-slate-800/60 dark:to-slate-800/40 backdrop-blur-xl rounded-2xl border border-white/20 dark:border-slate-700/30 shadow-xl"></div>
          <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-br from-emerald-400/20 to-green-400/20 rounded-full blur-3xl opacity-60"></div>
          <div className="relative z-10 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-500 to-green-500"></div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.attendanceRate")}</h3>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {d.attendees} / {d.registrations} {t("webinar.registrantsAttended")}
                </p>
              </div>
              <span className="text-md font-black text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1 rounded-xl">{attendancePct}%</span>
            </div>
            <div className="h-[190px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.attendanceRateData} margin={{ top: 22, right: 8, left: -20, bottom: 0 }} barSize={48}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                  <XAxis dataKey="name" fontSize={11} stroke="currentColor" />
                  <YAxis fontSize={11} stroke="currentColor" unit="%" domain={[0, 100]} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="%" radius={[6, 6, 0, 0]}>
                    {d.attendanceRateData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Live Viewer Timeline */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-slate-800/80 dark:via-slate-800/60 dark:to-slate-800/40 backdrop-blur-xl rounded-2xl border border-white/20 dark:border-slate-700/30 shadow-xl"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-3xl opacity-60"></div>
          <div className="relative z-10 p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.liveViewerTimeline")}</h3>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{t("webinar.concurrentViewers")}</p>
            <div className="h-[190px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.viewerTimeline} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                  <XAxis dataKey="time" fontSize={11} stroke="currentColor" />
                  <YAxis fontSize={11} stroke="currentColor" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone" dataKey="viewers" name="Viewers"
                    stroke={COLORS[0]} strokeWidth={2.5} dot={false}
                    activeDot={{ r: 5, fill: COLORS[0] }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Geographic Distribution */}
      <div className="relative group">
        <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-slate-800/80 dark:via-slate-800/60 dark:to-slate-800/40 backdrop-blur-xl rounded-2xl border border-white/20 dark:border-slate-700/30 shadow-xl"></div>
        <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-indigo-400/20 to-purple-400/20 rounded-full blur-3xl opacity-60"></div>
        <div className="relative z-10 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"></div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.geographicDistribution")}</h3>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{t("webinar.attendeesByCountry")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div className="h-[165px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={d.geographicData} layout="vertical"
                  margin={{ top: 4, right: 36, left: 8, bottom: 0 }} barSize={14}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" horizontal={false} />
                  <XAxis type="number" fontSize={10} stroke="currentColor" />
                  <YAxis type="category" dataKey="country" fontSize={10} stroke="currentColor" width={94} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Attendees" radius={[0, 4, 4, 0]}>
                    {d.geographicData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3 p-4 bg-card/30 dark:bg-slate-700/30 rounded-xl backdrop-blur-sm">
              {d.geographicData.map((g, i) => (
                <div key={g.country} className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300 flex-1 truncate">{g.country}</span>
                  <span className="text-sm font-bold text-slate-800 dark:text-white">{g.count}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 w-12 text-right">{g.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Survey + Polls + Q&A cards - plain analytics style (no gradient bg) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          { label: t("webinar.feedbackSurvey"), value: d.feedbackResponses, route: "feedback", icon: <MessageSquare className="w-5 h-5 text-rose-500" />, desc: t("webinar.responsesCollected") },
          { label: t("webinar.polls"), value: d.pollResponses, route: "polls", icon: <ClipboardList className="w-5 h-5 text-indigo-500" />, desc: t("webinar.responsesCollected") },
          { label: t("webinar.qaQuestions"), value: 24, route: "qa", icon: <MessageSquare className="w-5 h-5 text-emerald-500" />, desc: t("webinar.questionsSubmitted") },
        ].map(c => (
          <button
            key={c.label}
            onClick={() => navigate(`/webinar/${id}/analytics/${c.route}`)}
            className="group bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl px-4 py-4 shadow-sm hover:shadow-md hover:border-primary/30 transition-all text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                {c.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="text-2xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white leading-none truncate">
                    {c.value}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary transition-colors" />
                </div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate">
                  {c.label}
                </div>
                <div className="text-xs text-slate-400 mt-0.5 truncate">
                  {c.desc}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Reactions + Session Summary - half width each */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Reactions */}
        <div className="bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 flex items-center justify-center text-xl leading-none">
              😊
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.reactions")}</div>
              <div className="text-xs text-slate-400">{t("webinar.emojiResponsesFromAttendees")}</div>
            </div>
          </div>
          <div className="flex flex-wrap justify-between gap-4">
            {d.reactions.map(r => (
              <div key={r.label} className="flex flex-col items-center gap-1 min-w-[36px]">
                <span className="text-2xl leading-none">{r.emoji}</span>
                <span className="text-sm font-bold text-slate-800 dark:text-white">{r.count}</span>
                <span className="text-[10px] text-slate-400">{r.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Session Summary */}
        <div className="bg-card dark:bg-card rounded-2xl border border-stroke-100 dark:border-border dark:border-border shadow-sm p-5">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-3">{t("webinar.sessionSummary")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: t("webinar.avgWatchTime"), value: "64 min", icon: <Clock className="w-4 h-4 text-blue-500" />, bg: "bg-blue-50 dark:bg-blue-900/20" },
              { label: t("webinar.peakViewers"), value: "89", icon: <Users className="w-4 h-4 text-emerald-500" />, bg: "bg-emerald-50 dark:bg-emerald-900/20" },
              { label: t("webinar.totalDuration"), value: "90 min", icon: <Video className="w-4 h-4 text-purple-500" />, bg: "bg-purple-50 dark:bg-purple-900/20" },
              { label: t("webinar.engagementScore"), value: "87%", icon: <ClipboardList className="w-4 h-4 text-amber-500" />, bg: "bg-amber-50 dark:bg-amber-900/20" },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${s.bg}`}>{s.icon}</div>
                <div>
                  <div className="text-base font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{s.value}</div>
                  <div className="text-xs text-slate-400">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
